import React, {useEffect} from 'react'
import PropTypes from 'prop-types'
import {Box, SimpleGrid} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useProducts} from '@salesforce/commerce-sdk-react'
import ProductTile from '../../../../product-tile'
import {useGlobaleContext} from '../../../../../contexts/globale'
import {SELECTED_ATTRIBUTES_WIDGET_PRODUCTS} from '../../../../../constants'
import {Swiper, SwiperSlide} from 'swiper/react'
import 'swiper/css'
import 'swiper/css/navigation'
import {Navigation} from 'swiper/modules'
import CategoryTile from './categoryTile'
import useSiteCategories from '../../../../../hooks/use-site-categories'

export const ProductsList = ({
    setProducts,
    onTileClick = () => {},
    changeToGrid = false,
    ...rest
}) => {
    const selectedAttributes = SELECTED_ATTRIBUTES_WIDGET_PRODUCTS
    const {countryISO, currencyCode} = useGlobaleContext()
    const productIds = []
    let index = 1

    while (`product${index}` in rest) {
        productIds.push(rest[`product${index}`])
        delete rest[`product${index}`]
        index++
    }

    let catIndex = 1
    const categoryIds = []
    while (`category${catIndex}` in rest) {
        categoryIds.push(rest[`category${catIndex}`])
        delete rest[`category${catIndex}`]
        catIndex++
    }

    const {data: products} = useProducts(
        {
            parameters: {
                ids: productIds.join(','),
                expand: ['availability', 'links', 'promotions', 'images', 'prices', 'variations'],
                perPricebook: true,
                select: selectedAttributes,
                allVariationProperties: true,
                allImages: true,
                c_selectedCountry: countryISO,
                c_selectedCurrency: currencyCode
            }
        },
        {
            enabled: Boolean(productIds.length)
        }
    )

    const {data: categories} = useSiteCategories({
        parameters: {
            ids: categoryIds.join(','),
            levels: 0, // No subcategories
            locale: 'en-US'
        }
    })

    useEffect(() => {
        if (setProducts && products?.data) {
            setProducts(products.data)
        }
    }, [products])

    if (productIds.length === 0 && categoryIds.length === 0) {
        return null
    }

    const productList = products?.data?.slice(0, 15) || []
    const categoryList = categories?.data?.slice(0, 15) || []
    const mergedList = [...categoryList, ...productList]

    return (
        <Box
            marginBottom={{base: '5px', lg: '0'}}
            sx={{
                ...{
                    '& .slick-slider': {
                        paddingTop: '0 !important'
                    },
                    '& .swiper-horizontal': {
                        paddingTop: '0 !important'
                    },
                    '& .swiper-slide': {
                        maxWidth: {base: '77px', md: '100px'}
                    },
                    '& .slick-slide': {
                        height: {base: '96px', md: '100px'}
                    },
                    '& div[data-testid="product-scroller"]': {
                        padding: '0 !important'
                    },
                    '& .slick-track': {
                        display: 'inline-flex',
                        padding: {base: '20px 30px', md: 'unset'}
                    },
                    '& .product-name': {
                        fontSize: changeToGrid ? '11px' : '15px',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        color: rest.textColor || 'black.100',
                        fontFamily: rest.textFontFamily || 'caslon',
                        textAlign: rest.textAlignment || 'center',
                        minHeight: 'unset',
                        fontWeight: '300',
                        margin: '4px 0 0 0'
                    },
                    '& div[data-testid="product-tile-image"]': {
                        background: rest.backgroundColor || 'white'
                    },
                    '& .tile-box': {
                        display: 'flex',
                        justifyContent: 'center'
                    },
                    '& .image-wrapper': {
                        width: {
                            base: changeToGrid ? '42px' : '77px',
                            sm: changeToGrid ? '75px' : '77px',
                            md: '100px'
                        }
                    },
                    '& .info-wrapper': {
                        margin: '0'
                    }
                }
            }}
        >
            {changeToGrid ? (
                <>
                    <SimpleGrid
                        className="condensed-grid"
                        columns={5}
                        spacingX="10px"
                        spacingY="15px"
                        padding="4px 10px 0 10px"
                    >
                        {mergedList?.map((list, index) => (
                            <>
                                {/* category list */}
                                {list?.parentCategoryTree && (
                                    <CategoryTile
                                        category={list}
                                        textFontFamily={rest.textFontFamily}
                                        textColor={rest.textColor}
                                        textAlignment={rest.textAlignment}
                                        backgroundColor={rest.backgroundColor}
                                        title={rest[`category${index + 1}Name`]}
                                        isGrid={true}
                                        image={rest[`category${index + 1}Img`]}
                                    />
                                )}
                                {/* product list */}
                                {list?.c_masterVariantID && (
                                    <ProductTile
                                        key={`${list.productId || list.id}-${index}`}
                                        product={list}
                                        index={index}
                                        showName={true}
                                        showPrice={false}
                                        showQuickAdd={false}
                                        hideVariations={true}
                                        showImageSlider={false}
                                        showBadge={false}
                                        showPromotions={false}
                                        preventTileClick={false}
                                        isplp={false}
                                    />
                                )}
                            </>
                        ))}
                    </SimpleGrid>
                </>
            ) : (
                <Box
                    width="100%"
                    className="swiper-parent"
                    sx={{
                        '.swiper': {
                            paddingRight: '20px',
                            paddingTop: 0,
                            marginTop: 0,

                            '&:hover': {
                                '.swiper-button-prev, .swiper-button-next': {
                                    opacity: 1
                                }
                            }
                        },
                        '.swiper-wrapper': {
                            paddingLeft: {base: '15px', sm: '30px', md: 0},
                            display: 'inline-flex'
                        },
                        '.swiper-slide': {
                            width: '77px',
                            marginRight: '8px',
                            '&:last-child': {
                                marginRight: 0
                            }
                        }
                    }}
                >
                    <Swiper
                        navigation={false}
                        slidesPerView={'auto'}
                        spaceBetween={10}
                        modules={[Navigation]}
                    >
                        {mergedList?.map((list, index) => (
                            <SwiperSlide
                                key={`${list?.id || index}-${index}`}
                                onClick={onTileClick}
                            >
                                {/* category list */}
                                {list?.parentCategoryTree && (
                                    <CategoryTile
                                        category={list}
                                        textFontFamily={rest.textFontFamily}
                                        textColor={rest.textColor}
                                        textAlignment={rest.textAlignment}
                                        backgroundColor={rest.backgroundColor}
                                        title={rest[`category${index + 1}Name`]}
                                        isGrid={false}
                                        image={rest[`category${index + 1}Img`]}
                                    />
                                )}
                                {/* product list */}
                                {list?.c_masterVariantID && (
                                    <ProductTile
                                        key={`${list.productId || list.id}-${index}`}
                                        product={list}
                                        index={index}
                                        showName={true}
                                        showPrice={false}
                                        showQuickAdd={false}
                                        hideVariations={true}
                                        showImageSlider={false}
                                        showBadge={false}
                                        showPromotions={false}
                                        preventTileClick={false}
                                        isplp={false}
                                    />
                                )}
                            </SwiperSlide>
                        ))}
                    </Swiper>
                </Box>
            )}
        </Box>
    )
}

ProductsList.propTypes = {
    title: PropTypes.string,
    setProducts: PropTypes.any,
    titleTag: PropTypes.string,
    onTileClick: PropTypes.func,
    changeToGrid: PropTypes.bool
}

export default ProductsList
